import React, { useState, useEffect, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import { Warning } from '@material-ui/icons'
import Notification from 'components/Notification'
import { checkPermission, getPermissionCodeByPath } from 'helpers/permission'
import { jobListPromise, jobSetActivePromise } from 'api/Job'

// mq writes these (db/patch-2018.sql). -1 is also what stopping a job writes: it falls outside the
// `find_not_end` scope, so the worker stops registering the job at its next boot, and the per-fire
// row re-read stops running it before then.
const StatusText = { '-1': 'Stopped', 0: 'Ready', 1: 'Running', 2: 'Failed' }

// Cron in the `job` table is UTC, and the worker host runs UTC. Showing only local time is how a
// schedule ends up read 8 hours off, so every instant here is rendered both ways and labelled.
function BothZones ({ value }) {
  if (!value) return <>-</>

  // Parsed as UTC rather than parsed-then-converted: the API sends these with a Z today, but an
  // offset-less datetime would be read as browser-local and both labels would then name the wrong
  // instant -- on the one page where reading a time wrong is the whole failure mode.
  const at = moment.utc(value)

  return (
    <>
      <div>{at.format('DD/MM/YYYY HH:mm')} UTC</div>
      <div>{at.local().format('DD/MM/YYYY HH:mm')} local</div>
    </>
  )
}

export default function JobList () {
  const [jobs, setJobs] = useState([])
  const [confirming, setConfirming] = useState(null)
  const [notification, setNotification] = useState(null)
  // The dialog closes as soon as it is confirmed, so without this the row still offers the same
  // action while its request is in flight and a second click sends a parallel write.
  const [pendingId, setPendingId] = useState(null)

  // A write here can outlive the page: ApiClient sends the browser to /login on a 401 (FEA-309), so
  // a stop issued on an expired session unmounts this component while its request is still in
  // flight. Painting the result afterwards is a React warning and, worse, a toast about a write
  // that never happened.
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const load = useCallback(async () => {
    const rows = await jobListPromise() || []
    if (mounted.current) setJobs(rows)
  }, [])

  useEffect(() => { load() }, [load])

  // One code for both directions, matching the single endpoint: an admin who can stop a production
  // schedule can start it again.
  const canWrite = checkPermission(getPermissionCodeByPath(window.location.pathname, ['active']))

  async function handleConfirm () {
    const { job, active } = confirming
    const verb = active ? 'start' : 'stop'
    setConfirming(null)
    setPendingId(job.id)

    try {
      const resp = await jobSetActivePromise(job.id, active) || {}
      // The API reports every way this can look like it worked and do nothing: a stop lands after
      // the current run, and a start can hit a missing handler, a schedule the worker never
      // registered, or a spent repeat budget. Swallowing that is how a no-op passes for success.
      if (mounted.current) {
        setNotification(resp.warning
          ? { type: 'warning', message: `${job.name}: ${resp.warning}` }
          : { type: 'success', message: `${job.name} ${active ? 'started' : 'stopped'}.` })
      }
    } catch (err) {
      // The status is the useful half: 403 means the permission code is not granted, 401 means the
      // session is gone and the browser is already on its way to /login. superagent's `message` on
      // its own reads as a bare "Forbidden".
      const detail = err.status ? `HTTP ${err.status}` : err.message
      if (mounted.current) {
        setNotification({ type: 'error', message: `Could not ${verb} ${job.name}: ${detail}` })
      }
    }

    await load()
    if (mounted.current) setPendingId(null)
  }

  return (
    <div className={'root'}>
      <div className={'content'}>
        <Card>
          <CardHeader
            title='Job schedule'
            subheader={'Read-only apart from Stop and Start. A schedule and the handler it names ship together, so adding, editing or rescheduling one is a db/data patch. Live schedules first, newest first; stopped ones at the bottom.'}
          />
          <CardContent className={'table-content'}>
            <PerfectScrollbar>
              <div className={'inner'}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Next fire</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last run</TableCell>
                      <TableCell>Runs</TableCell>
                      <TableCell>Attempts</TableCell>
                      <TableCell>Auto restart</TableCell>
                      <TableCell>Instance</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {jobs.map(job => (
                      <TableRow key={job.id} hover>
                        <TableCell>{job.id}</TableCell>
                        <TableCell>
                          {job.name}
                          {/* A name with no handler key never runs and reports nothing anywhere --
                              no error, no log. This column is the only place it is visible. */}
                          {!job.handlerExists && (
                            <Tooltip title='No handler of this name is deployed — this job never runs and reports nothing'>
                              <Warning color='error' fontSize='small' />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{job.description}</TableCell>
                        <TableCell>{job.trigger}</TableCell>
                        <TableCell><BothZones value={job.nextFireAtUtc} /></TableCell>
                        <TableCell>
                          {StatusText[job.status] || job.status}
                          {/* Ready but not registered: the worker registers schedules at boot only,
                              so this row is waiting for the next one however ready it looks. */}
                          {job.status >= 0 && job.registered === false && (
                            <Tooltip title='The running worker has not registered this schedule — it starts firing at the next mq boot'>
                              <Warning color='action' fontSize='small' />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell><BothZones value={job.lastTimeWindow} /></TableCell>
                        <TableCell>{job.finishCounter}</TableCell>
                        <TableCell>{job.attempts}</TableCell>
                        <TableCell>{job.autoRestart ? 'TRUE' : 'FALSE'}</TableCell>
                        <TableCell>{job.instanceId}</TableCell>
                        <TableCell>
                          {canWrite && (job.status >= 0 ? (
                            <Button
                              size='small'
                              color='secondary'
                              disabled={pendingId === job.id}
                              onClick={() => setConfirming({ job, active: false })}>
                              Stop
                            </Button>
                          ) : (
                            <Button
                              size='small'
                              color='primary'
                              disabled={pendingId === job.id}
                              onClick={() => setConfirming({ job, active: true })}>
                              Start
                            </Button>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>
          </CardContent>
        </Card>
      </div>

      {/* The console points at production, and this is the one page where a stray click changes
          what production runs on a schedule.

          Mounted only while there is something to confirm. Keeping it mounted with `open={false}`
          still renders its children, and every one of them would then have to cope with a null
          confirming row -- and a null there throws out of MUI and takes the whole tree down. */}
      {confirming && (
        <Dialog open onClose={() => setConfirming(null)}>
          <DialogTitle>{confirming.active ? 'Start this job?' : 'Stop this job?'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <b>{confirming.job.name}</b> ({confirming.job.trigger}){' '}
              {confirming.active
                ? (confirming.job.registered === false
                  ? 'will be put back in service, but the running worker does not hold this schedule — it only starts firing at the next mq boot.'
                  : 'will be put back in service and fires again at its next scheduled run.')
                : 'will stop firing from its next scheduled run. Its history is kept and it can be started again from this page.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              color={confirming.active ? 'primary' : 'secondary'}
              variant='contained'
              disabled={pendingId === confirming.job.id}
              onClick={handleConfirm}>
              {confirming.active ? 'Start job' : 'Stop job'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Notification
        open={!!notification}
        type={(notification || {}).type || 'info'}
        message={(notification || {}).message || ''}
        onClose={() => setNotification(null)}
      />
    </div>
  )
}
